import React from 'react';
import { CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const SizeGuide = () => {
  const sizes = [
    {
      size: '5\' x 10\'',
      sqft: '50 sq ft',
      comparable: 'Walk-in Closet',
      idealFor: 'Boxes, twin mattress, seasonal decor'
    },
    {
      size: '8\' x 10\'',
      sqft: '80 sq ft',
      comparable: 'Mid-size Bedroom',
      idealFor: 'Furnishings of a one-bedroom apartment with appliances'
    },
    {
      size: '10\' x 10\'',
      sqft: '100 sq ft',
      comparable: 'Average Bedroom',
      idealFor: '1-bedroom apartment (King bed + appliances)'
    },
    {
      size: '10\' x 15\'',
      sqft: '150 sq ft',
      comparable: 'Large Master Bedroom',
      idealFor: '2-bedroom apartment contents'
    },
    {
      size: '10\' x 20\'',
      sqft: '200 sq ft',
      comparable: 'Single-Car Garage',
      idealFor: '3-bedroom home or contractor tools'
    },
    {
      size: '10\' x 25\'',
      sqft: '250 sq ft',
      comparable: 'Large Garage',
      idealFor: 'Fully furnished 3-bedroom home, plus garage items'
    },
    {
      size: '10\' x 30\'',
      sqft: '300 sq ft',
      comparable: 'Extra Large Garage',
      idealFor: '4-5 bedroom house or commercial inventory'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage Unit Sizes & Pricing Guide | Interskate Self Storage - Lewisville, TX"
        description="Compare storage unit sizes at Interskate Self Storage in Lewisville, TX. From 5x10 walk-in closet size to 10x30 extra-large units. Find the right fit for your needs."
        canonical="/size-guide"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="size-guide-title">
            Storage Unit Sizes Available
          </h1>
          <p className="text-xl text-green-100">
            Don't pay for space you don't need. Use our guide to find the perfect fit.
          </p>
        </div>
      </section>

      {/* Size Guide Table */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sizes.map((unit, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{unit.size}</h3>
                      <p className="text-xl text-green-600 font-semibold mb-1">{unit.sqft}</p>
                      <p className="text-gray-600">Comparable to: <strong>{unit.comparable}</strong></p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Ideal For:</h4>
                    <p className="text-gray-700 text-lg">{unit.idealFor}</p>
                  </div>

                  <div className="mt-6">
                    <a
                      href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        if (window.gtag) {
                          window.gtag('event', 'conversion_intent', {
                            'event_category': 'lead',
                            'event_label': 'size_guide_check_availability',
                            'facility_name': 'Interskate Self Storage'
                          });
                        }
                      }}
                      className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Check Availability
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Choosing the Right Size
          </h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>When in doubt, go bigger.</strong> It's easier to have extra space than to upgrade later.
              </p>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Stack vertically.</strong> Use shelving to maximize your space and keep items organized.
              </p>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Plan for growth.</strong> If you're storing business inventory, consider future needs.
              </p>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Need help deciding?</strong> Call us at (469) 251-6255 and we'll help you choose.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Reserve Your Unit?</h2>
          <p className="text-xl text-green-100 mb-8">
            Check today's prices and rent online in just 2 minutes.
          </p>
          <a
            href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (window.gtag) {
                window.gtag('event', 'conversion_intent', {
                  'event_category': 'lead',
                  'event_label': 'size_guide_cta_check_prices',
                  'facility_name': 'Interskate Self Storage'
                });
              }
            }}
            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            data-testid="cta-button"
          >
            Check Today's Prices
          </a>
        </div>
      </section>
    </div>
  );
};

export default SizeGuide;